/**
 * Audio Helper for Mobile Compatibility
 *
 * iOS/Safari and some Android browsers block autoplay unless audio
 * is triggered directly by a synchronous user interaction.
 *
 * This helper "unlocks" both AudioContext and HTMLAudioElement during user gestures,
 * allowing audio playback later (even after async operations).
 */

import { logger } from '@/lib/logger';

// Singleton AudioContext for iOS/mobile
let audioContext: AudioContext | null = null;
let isAudioUnlocked = false;

// Persistent audio element for iOS playback
let persistentAudio: HTMLAudioElement | null = null;
let isHtmlAudioUnlocked = false;

// Silent MP3 data URL (minimal valid MP3)
const SILENT_MP3 = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAABgAlpaWlpaWlpaWlpaWlpaWlpaWlpaWlpaW////////////////////////////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQAAAAAAAAAAAAYAAAAAAAAAAAAAAAA/+MYxAALIAZBGAEAAAAA";

/**
 * Call this during any user interaction (tap, click) to unlock audio
 * for later playback on mobile. Must be called synchronously from
 * the user gesture handler (not inside a setTimeout or Promise).
 */
/**
 * Call this during any user interaction (tap, click) to unlock audio
 * for later playback on mobile. This only unlocks HTMLAudioElement for TTS.
 * Does NOT create AudioContext - that would interfere with microphone.
 */
export function unlockAudio(): void {
  // Only unlock HTMLAudioElement for TTS autoplay
  // Do NOT create AudioContext here - it conflicts with getUserMedia on iOS
  unlockHtmlAudio();
}

/**
 * Unlock HTML Audio element on user gesture.
 * This MUST be called synchronously from a user tap/click.
 * Creates a persistent audio element and plays a silent sound.
 */
export function unlockHtmlAudio(): HTMLAudioElement | null {
  if (isHtmlAudioUnlocked && persistentAudio) {
    return persistentAudio;
  }
  
  
  try {
    // Create persistent audio element
    persistentAudio = new Audio();
    
    // Play silent MP3 to unlock - MUST happen in user gesture
    persistentAudio.src = SILENT_MP3;
    
    const playPromise = persistentAudio.play();
    
    if (playPromise) {
      playPromise
        .then(() => {
          isHtmlAudioUnlocked = true;
          // Keep audio element ready for reuse by pausing after unlock
          persistentAudio!.volume = 0;
          persistentAudio!.pause();
        })
        .catch((err) => {
          logger.warn("AudioHelper", "HTML Audio unlock failed:", err);
        });
    }
    
    return persistentAudio;
  } catch (e) {
    logger.warn("AudioHelper", "Failed to create persistent audio:", e);
    return null;
  }
}

/**
 * Check if HTML Audio has been unlocked
 */
export function isHtmlAudioReady(): boolean {
  return isHtmlAudioUnlocked;
}

/**
 * Get the persistent audio element
 */
export function getPersistentAudio(): HTMLAudioElement | null {
  return persistentAudio;
}

/**
 * Reset the unlock state (useful for testing)
 */
export function resetAudioUnlock(): void {
  if (audioContext) {
    audioContext.close().catch(() => {});
    audioContext = null;
  }
  isAudioUnlocked = false;
  persistentAudio = null;
  isHtmlAudioUnlocked = false;
}
